import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ProviderAccountModel, ProviderAccountRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['providerAccounts']

type ProviderAccountFilters = {
  agency_id?: number,
  agency_string?: string,
  provider_id?: number,
}

type UseProviderAccountsOptions = {
  filters?: ProviderAccountFilters,
  performHttpRequests?: boolean,
  requestOptions?: ProviderAccountRequestOptions,
}

function useProviderAccounts(options: UseProviderAccountsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    agency_string: filterAgencyString,
    provider_id: filterProviderId,
  } = filters

  const {
    updatedEntities: { providerAccounts: providerAccountsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { agencies, providerAccounts } = useSelector(reduxState => reduxState.entities)

  const filteredProviderAccounts = useMemo(() => {
    const filtered = Object.values(providerAccounts).filter((providerAccount: ProviderAccountModel) => {
      const {
        agency_id, provider_id,
      } = providerAccount

      const agencyTitle = agencies[agency_id]?.title

      const matchAgencyStringId = agency_id?.toString() === filterAgencyString
      const matchAgencyStringTitle = matchFilterString(agencyTitle, filterAgencyString)
      const matchAgencyString = matchAgencyStringId || matchAgencyStringTitle

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)
      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)

      return matchAgencyId && matchAgencyString && matchProviderId
    })

    return sortArrayBy(filtered, 'asc', 'id')
  }, [providerAccountsUpdatedAt, JSON.stringify(filters)])

  const filteredProviderAccountsCount = filteredProviderAccounts.length
  const hasFilteredProviderAccounts = !!filteredProviderAccountsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProviderAccountsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviderAccounts } = useReduxAction(
    'providerAccounts',
    'loadProviderAccounts',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderAccounts,
    filteredProviderAccountsCount,
    hasFilteredProviderAccounts,
    loading: loadingProviderAccounts,
  }
}

export default useProviderAccounts
